import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'src/config/routes';
import { useNotification } from 'src/contexts/NotificationContext';
import { useUser } from 'src/contexts/UserContext';
import { useSwitchUser } from 'src/queries/sessions';
import type { FamilyMemberResponse, User } from 'src/types/user.types';
import { markProfileChosen } from 'src/utils/sharedDevice';
import { isParentUser } from 'src/utils/userRole';
import { getLocalStorageUserID } from 'src/utils/usersUtils';

type FamilyMember = User | FamilyMemberResponse;

const useFamilyProfileSwitch = (familyMembers: FamilyMember[] | undefined) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showError } = useNotification();
  const { me } = useUser();
  const { mutateAsync: switchUser } = useSwitchUser();

  const switchTo = useCallback(
    async (userId: number, path: string) => {
      try {
        await switchUser(userId);
        navigate(path);
      } catch (error) {
        showError({ error, message: t('login.familyUserSelect.switchError') });
        throw error;
      }
    },
    [switchUser, navigate, showError, t],
  );

  const selectProfile = useCallback(
    (user: FamilyMember) => switchTo(user.id, ROUTES.ROOT.path),
    [switchTo],
  );

  const parent = isParentUser(me)
    ? me
    : familyMembers?.find(member => isParentUser(member) && !!member.firstname);

  const addProfile = useCallback(async () => {
    if (!parent) return;
    if (parent.id === me.id && getLocalStorageUserID() === me.id) {
      navigate(ROUTES.PARENT_CHILD_FIRSTNAME.path);
      markProfileChosen();
      return;
    }
    await switchTo(parent.id, ROUTES.PARENT_CHILD_FIRSTNAME.path);
  }, [parent, me.id, navigate, switchTo]);

  return { selectProfile, addProfile: parent ? addProfile : undefined };
};

export default useFamilyProfileSwitch;
